import { useEffect, useState } from "react";
import {
  Search, ExternalLink, Eye, EyeOff, Save, RotateCcw, Edit3, X, Loader2, CheckCircle2,
} from "lucide-react";
import { accessApi } from "@/access/api";

/* Pages · SEO — editable. Saves overrides server-side; the public site reads merged
   values via GET /api/page-seo and updates document.title + meta[description] live. */

export default function AccessPages() {
  const [pages, setPages] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // path string
  const [draft, setDraft] = useState({ title: "", description: "", robots: "" });
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState("");

  const load = () => accessApi.pages().then(({ data }) => setPages(data));
  useEffect(() => { load(); }, []);

  const filtered = pages.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      p.path.toLowerCase().includes(s) ||
      (p.title || "").toLowerCase().includes(s) ||
      (p.description || "").toLowerCase().includes(s)
    );
  });

  const startEdit = (p) => {
    setEditing(p.path);
    setDraft({ title: p.title, description: p.description, robots: p.robots });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({ title: "", description: "", robots: "" });
  };

  const save = async (path) => {
    setSaving(true);
    try {
      const { data } = await accessApi.savePage({ path, ...draft });
      setPages(data);
      setSavedFlash(path);
      setTimeout(() => setSavedFlash(""), 2000);
      cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  const reset = async (path) => {
    if (!window.confirm(`Reset ${path} back to its default title / description?`)) return;
    const { data } = await accessApi.resetPage(path);
    setPages(data);
    setSavedFlash(path);
    setTimeout(() => setSavedFlash(""), 2000);
  };

  return (
    <div data-testid="access-pages" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-300">Pages · SEO</p>
          <h1 className="mt-1.5 font-display text-3xl font-black tracking-tight">{pages.length} pages.</h1>
          <p className="mt-1 text-[13px] text-white/55">
            Click any row to edit its title &amp; description — changes propagate to the live site within seconds.
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter…"
            data-testid="pages-search"
            className="rounded-lg border border-white/15 bg-white/[0.04] py-1.5 pl-8 pr-3 text-[12px] outline-none placeholder:text-white/30 focus:border-violet-400/60"
          />
        </div>
      </header>

      <div className="space-y-2">
        {filtered.map((p) => {
          const isEditing = editing === p.path;
          const isFlashing = savedFlash === p.path;
          return (
            <article
              key={p.path}
              data-testid={`pages-row-${p.path}`}
              className={`rounded-2xl border bg-white/[0.03] p-4 transition-all ${
                isEditing ? "border-violet-400/50 bg-violet-500/[0.05]" : "border-white/10 hover:border-white/20"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${p.robots?.includes("noindex") ? "bg-rose-500/15 text-rose-200" : "bg-emerald-500/15 text-emerald-200"}`}>
                    {p.robots?.includes("noindex") ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {p.robots?.includes("noindex") ? "noindex" : "indexed"}
                  </span>
                  <span className="font-mono text-[12.5px] text-violet-200">{p.path}</span>
                  {p.is_overridden && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-violet-200">
                      Edited
                    </span>
                  )}
                  {isFlashing && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-emerald-300">
                      <CheckCircle2 className="h-3 w-3" /> Saved
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {!isEditing ? (
                    <>
                      {p.is_overridden && (
                        <button
                          onClick={() => reset(p.path)}
                          data-testid={`pages-reset-${p.path}`}
                          className="inline-flex h-7 items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 text-[11px] font-bold text-white/70 hover:bg-white/[0.08]"
                        >
                          <RotateCcw className="h-3 w-3" /> Reset
                        </button>
                      )}
                      <button
                        onClick={() => startEdit(p)}
                        data-testid={`pages-edit-${p.path}`}
                        className="inline-flex h-7 items-center gap-1 rounded-md bg-violet-500/20 px-2.5 text-[11px] font-bold text-violet-100 hover:bg-violet-500/30"
                      >
                        <Edit3 className="h-3 w-3" /> Edit
                      </button>
                      <a
                        href={p.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-white/55 hover:bg-white/[0.06] hover:text-white"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => save(p.path)}
                        disabled={saving}
                        data-testid={`pages-save-${p.path}`}
                        className="inline-flex h-7 items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-fuchsia-600 px-2.5 text-[11px] font-bold text-white disabled:opacity-60"
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 text-[11px] font-bold text-white/70 hover:bg-white/[0.08]"
                      >
                        <X className="h-3 w-3" /> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              {!isEditing ? (
                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr,1.6fr]">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Title</p>
                    <p className="mt-1 font-bold leading-snug">{p.title}</p>
                    <p className="mt-0.5 text-[10.5px] text-white/40">{(p.title || "").length} chars</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Description</p>
                    <p className="mt-1 text-white/75 leading-snug">{p.description}</p>
                    <p className="mt-0.5 text-[10.5px] text-white/40">{(p.description || "").length} chars</p>
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  <FieldEditor label="Title" hint={`${draft.title.length} chars · best between 50 and 60`}>
                    <input
                      value={draft.title}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      data-testid={`pages-edit-title-${p.path}`}
                      className="block w-full rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-[14px] outline-none focus:border-violet-400/60"
                    />
                  </FieldEditor>
                  <FieldEditor label="Description" hint={`${draft.description.length} chars · best between 140 and 160`}>
                    <textarea
                      value={draft.description}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      rows={3}
                      data-testid={`pages-edit-description-${p.path}`}
                      className="block w-full resize-y rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-[13px] outline-none focus:border-violet-400/60"
                    />
                  </FieldEditor>
                  <FieldEditor label="Robots">
                    <select
                      value={draft.robots}
                      onChange={(e) => setDraft({ ...draft, robots: e.target.value })}
                      data-testid={`pages-edit-robots-${p.path}`}
                      className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-[12.5px] outline-none focus:border-violet-400/60"
                    >
                      <option value="index, follow" className="bg-[#0a0a14]">index, follow</option>
                      <option value="noindex, follow" className="bg-[#0a0a14]">noindex, follow</option>
                      <option value="noindex, nofollow" className="bg-[#0a0a14]">noindex, nofollow</option>
                      <option value="index, nofollow" className="bg-[#0a0a14]">index, nofollow</option>
                    </select>
                  </FieldEditor>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

const FieldEditor = ({ label, hint, children }) => (
  <label className="block">
    <span className="mb-1 inline-flex items-center justify-between gap-3 w-full">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">{label}</span>
      {hint && <span className="text-[10px] text-white/35">{hint}</span>}
    </span>
    {children}
  </label>
);
